import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HiCheck, HiX } from "react-icons/hi";
import ApplicantModal from "../ApplicantModal/ApplicantModal";
import DMInvitePopup from "../../../../../components/DMInvitePopup/DMInvitePopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApplicantsCard = ({ roleId, projectId, roleTitle, compact = false }) => {
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isDMOpen, setIsDMOpen] = useState(false);
  const navigate = useNavigate();

  const extractUsername = (email) => (email ? email.split("@")[0] : "Unknown");

  const getDisplayName = (applicant) => {
    if (applicant.firstName || applicant.lastName)
      return `${applicant.firstName || ""} ${applicant.lastName || ""}`.trim();
    return extractUsername(applicant.userId);
  };

  const formatDate = (timestamp) => {
    let dateObj = null;
    if (timestamp?._seconds) dateObj = new Date(timestamp._seconds * 1000);
    else if (timestamp?.seconds) dateObj = new Date(timestamp.seconds * 1000);
    else if (typeof timestamp === "string") dateObj = new Date(timestamp);
    else if (timestamp instanceof Date) dateObj = timestamp;

    return dateObj?.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }) || "N/A";
  };

  useEffect(() => {
    const fetchApplicants = async () => {
      if (!roleId) return;
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(`${BACKEND_URL}/applications/${roleId}/applicants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (res.ok) {
          const enriched = await Promise.all(
            data.applicants.map(async (a) => {
              try {
                const imgRes = await fetch(
                  `${BACKEND_URL}/user/${encodeURIComponent(a.userId)}/profile-image?timestamp=${Date.now()}`
                );
                const imgData = await imgRes.json();
                return {
                  ...a,
                  profileImage: imgData.profileImage?.startsWith("http")
                    ? imgData.profileImage
                    : "https://via.placeholder.com/50",
                };
              } catch {
                return { ...a, profileImage: "https://via.placeholder.com/50" };
              }
            })
          );
          setApplicants(enriched);
        }
      } catch (err) {
        console.error("Error fetching applicants:", err);
      }
    };
    fetchApplicants();
  }, [roleId]);

  const handleNavigate = (email) => {
    if (!email) return;
    navigate(`/${extractUsername(email)}`);
  };

  const handleDM = (applicant) => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) return alert("Login required to message.");
    setSelectedApplicant({ email: applicant.userId, name: getDisplayName(applicant) });
    setIsDMOpen(true);
  };

  const updateStatus = async (email, newStatus) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${BACKEND_URL}/applications/${roleId}/applicant/${email}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus, projectId }),
      });
      if (res.ok) {
        setApplicants((prev) =>
          prev.map((app) => (app.userId === email ? { ...app, status: newStatus } : app))
        );
      }
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  return (
    <>
      {compact ? (
        // Compact mode: just applicant rows
        <div className="space-y-2 overflow-x-hidden">
          {applicants.length > 0 ? (
            applicants.map((applicant) => (
              <div
                key={applicant.id}
                className="flex items-center justify-between bg-gray-50 rounded-lg p-2 cursor-pointer hover:bg-gray-100 transition overflow-hidden"
                onClick={() => setSelectedApplicant(applicant)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <img
                    src={applicant.profileImage}
                    alt="Avatar"
                    className="w-7 h-7 rounded-full border flex-shrink-0 object-cover"
                  />
                  <p className="text-xs font-medium text-gray-900 truncate min-w-0 max-w-[100px]">
                    {getDisplayName(applicant)}
                  </p>
                </div>
                <span
                  className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 max-w-[70px] truncate text-center"
                  style={{
                    backgroundColor:
                      applicant.status === "Approved"
                        ? "#DCFCE7"
                        : applicant.status === "Rejected"
                        ? "#FEE2E2"
                        : "#F3F4F6",
                    color:
                      applicant.status === "Approved"
                        ? "#15803D"
                        : applicant.status === "Rejected"
                        ? "#B91C1C"
                        : "#374151",
                  }}
                >
                  {applicant.status}
                </span>
              </div>
            ))
          ) : (
            <p className="text-xs text-gray-500 text-center">No applicants yet.</p>
          )}
        </div>
      ) : (
        // Full card mode
        <div className="w-96 h-52 flex flex-col flex-shrink-0 bg-white rounded-[20px] shadow-sm">
          <h2 className="text-sm font-semibold text-gray-800 mb-2 px-2 truncate">
            <span className="text-blue-600 font-bold">{roleTitle}</span>
          </h2>

          {applicants.length > 0 ? (
            <ul className="flex-1 overflow-y-auto overflow-x-hidden space-y-2 pr-2">
              {applicants.map((applicant) => (
                <li
                  key={applicant.id}
                  className="w-full flex items-center justify-between bg-gray-50 rounded-lg p-2 cursor-pointer hover:bg-gray-100 transition overflow-hidden"
                  onClick={() => setSelectedApplicant(applicant)}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <img
                      src={applicant.profileImage}
                      alt="Avatar"
                      className="w-7 h-7 rounded-full border flex-shrink-0 object-cover"
                    />
                    <p className="text-xs font-medium text-gray-900 truncate min-w-0 max-w-[100px]">
                      {getDisplayName(applicant)}
                    </p>
                  </div>
                  <span
                    className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 max-w-[70px] truncate text-center"
                    style={{
                      backgroundColor:
                        applicant.status === "Approved"
                          ? "#DCFCE7"
                          : applicant.status === "Rejected"
                          ? "#FEE2E2"
                          : "#F3F4F6",
                      color:
                        applicant.status === "Approved"
                          ? "#15803D"
                          : applicant.status === "Rejected"
                          ? "#B91C1C"
                          : "#374151",
                    }}
                  >
                    {applicant.status}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-xs">
              No applicants yet.
            </div>
          )}
        </div>
      )}

      {/* Selected ApplicantModal */}
      <ApplicantModal
        applicant={selectedApplicant}
        onClose={() => setSelectedApplicant(null)}
        getDisplayName={getDisplayName}
        formatDate={formatDate}
        handleDM={handleDM}
        updateStatus={updateStatus}
      />

    </>
  );


};

export default ApplicantsCard;
